import React, { useCallback, useEffect, useState } from "react";

import { Button, Col, Divider, Form, Modal, Row, Space } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { ColumnsType } from "antd/lib/table";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import interactionPlugin from "@fullcalendar/interaction";

import * as Common from "../../commons/common";
import * as Request from "../../commons/request";
import * as Types from "../../commons/typeDTO";
import * as TypeUtils from "../../commons/typeUtils";
import * as String from "../../commons/string";
import { useAsyncAxios, utilAxiosWithAuth } from "../../utils/customAxios";
import { AlertType, useAlert } from "../../provider/AlertProvider";

import DataTable from "../../components/DataTable";

import styles from "./RentPeakSeasonManagement.module.css";
import moment from "moment";
import ModalRentPeakSeason from "../../components/ModalRentPeakSeason";

function RentPeakSeasonManagement() {
    const [rentPeakSeasons, setRentPeakSeasons] = useState<Array<Types.RentPeakSeasonDto>>([]);
    const [currentRentPeakSeason, setCurrentRentPeakSeason] = useState<Types.RentPeakSeasonDto>();

    const [openModalRentPeakSeason, setOpenModalRentPeakSeason] = useState(false);
    const [holidays, setHolidays] = useState<Array<TypeUtils.Holidays>>([]);
    const [currentYear, setCurrentYear] = useState<number>(moment().year());

    const [form] = Form.useForm();
    const alert = useAlert();

    const columns: ColumnsType<Types.RentPeakSeasonDto> = [
        {
            align: "center",
            title: "ID",
            dataIndex: "rentPeakSeasonId",
            key: "rentPeakSeasonId",
        },
        {
            align: "center",
            title: "적용 시작일",
            dataIndex: "startDate",
            key: "startDate",
        },
        {
            align: "center",
            title: "적용 종료일",
            dataIndex: "endDate",
            key: "endDate",
        },
        {
            align: "center",
            title: " ",
            render: (_, row) => {
                return (
                    <>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdateRentPeakSeason(row);
                            }}
                        >
                            수정
                        </Button>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteRentPeakSeason(row);
                            }}
                        >
                            삭제
                        </Button>
                    </>
                );
            },
        },
    ];

    useEffect(() => {
        requestGetRentPeakSeasons();
    }, []);

    useEffect(() => {
        requestGetHolidays(currentYear);
    }, [currentYear]);

    const requestAxiosGetRentPeakSeasons = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_PEAK_SEASON_LIST_URL);
        return response.data;
    };

    const {
        loading: loadingGetRentPeakSeasons,
        error: errorGetRentPeakSeasons,
        data: resultGetRentPeakSeasons,
        execute: requestGetRentPeakSeasons,
    } = useAsyncAxios(requestAxiosGetRentPeakSeasons);

    useEffect(() => {
        if (resultGetRentPeakSeasons === null) return;
        console.log("resultGetRentPeakSeasons", resultGetRentPeakSeasons);

        const reverseRentPeakSeasons = [...resultGetRentPeakSeasons.rentPeakSeasons];
        setRentPeakSeasons(reverseRentPeakSeasons.reverse());

        form.setFieldValue("message", reverseRentPeakSeasons?.[0]?.message ?? "");
    }, [resultGetRentPeakSeasons]);

    useEffect(() => {
        if (errorGetRentPeakSeasons === null) return;
        console.log("errorGetRentPeakSeasons", errorGetRentPeakSeasons);
    }, [errorGetRentPeakSeasons]);

    const onUpdateRentPeakSeason = (rentPeakSeason: Types.RentPeakSeasonDto) => {
        setCurrentRentPeakSeason(rentPeakSeason);
        setOpenModalRentPeakSeason(true);
    };

    const requestAxiosRegisterUpdateRentPeakSeason = async (type: "register" | "update", value: any) => {
        const data = { ...value, message: rentPeakSeasons?.[0]?.message ?? "" };
        let response;
        if (type === "register") {
            response = await utilAxiosWithAuth().post(Request.RENT_PEAK_SEASON_URL, data);
        } else {
            response = await utilAxiosWithAuth().put(Request.RENT_PEAK_SEASON_URL + "/" + value.rentPeakSeasonId, data);
        }
        return response.data;
    };

    const {
        loading: loadingRegisterUpdateRentPeakSeason,
        error: errorRegisterUpdateRentPeakSeason,
        data: resultRegisterUpdateRentPeakSeason,
        execute: requestRegisterUpdateRentPeakSeason,
    } = useAsyncAxios(requestAxiosRegisterUpdateRentPeakSeason);

    useEffect(() => {
        if (resultRegisterUpdateRentPeakSeason === null) return;
        console.log("resultRegisterUpdateRentPeakSeason", resultRegisterUpdateRentPeakSeason);

        alert.setAlert(AlertType.SUCCESS, currentRentPeakSeason ? "성수기가 수정 되었습니다." : "성수기가 등록 되었습니다.", "");

        requestGetRentPeakSeasons();
    }, [resultRegisterUpdateRentPeakSeason]);

    useEffect(() => {
        if (errorRegisterUpdateRentPeakSeason === null) return;
        console.log("errorRegisterUpdateRentPeakSeason", errorRegisterUpdateRentPeakSeason);
    }, [errorRegisterUpdateRentPeakSeason]);

    const requestAxiosUpdateRentPeakSeasonMessage = async (message: string) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_PEAK_SEASON_URL + "/message", { message });
        return response.data;
    };

    const {
        loading: loadingUpdateRentPeakSeasonMessage,
        error: errorUpdateRentPeakSeasonMessage,
        data: resultUpdateRentPeakSeasonMessage,
        execute: requestUpdateRentPeakSeasonMessage,
    } = useAsyncAxios(requestAxiosUpdateRentPeakSeasonMessage);

    useEffect(() => {
        if (resultUpdateRentPeakSeasonMessage === null) return;
        console.log("resultUpdateRentPeakSeasonMessage", resultUpdateRentPeakSeasonMessage);

        alert.setAlert(AlertType.SUCCESS, "안내 문구가 수정되었습니다.", "");
        requestGetRentPeakSeasons();
    }, [resultUpdateRentPeakSeasonMessage]);

    useEffect(() => {
        if (errorUpdateRentPeakSeasonMessage === null) return;
        console.log("errorUpdateRentPeakSeasonMessage", errorUpdateRentPeakSeasonMessage);
    }, [errorUpdateRentPeakSeasonMessage]);

    const onDeleteRentPeakSeason = (rentPeakSeason: Types.RentPeakSeasonDto) => {
        Modal.confirm({
            title: "성수기 삭제",
            content: "성수기를 삭제 하시겠습니까?",
            okText: "확인",
            onOk() {
                requestDeleteRentPeakSeason(rentPeakSeason);
            },
            cancelText: "취소",
            onCancel() {},
            centered: true,
        });
    };

    const requestAxiosDeleteRentPeakSeason = async (rentPeakSeason: Types.RentPeakSeasonDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_PEAK_SEASON_URL + "/" + rentPeakSeason.rentPeakSeasonId);
        return response.data;
    };

    const {
        loading: loadingDeleteRentPeakSeason,
        error: errorDeleteRentPeakSeason,
        data: resultDeleteRentPeakSeason,
        execute: requestDeleteRentPeakSeason,
    } = useAsyncAxios(requestAxiosDeleteRentPeakSeason);

    useEffect(() => {
        if (resultDeleteRentPeakSeason === null) return;
        console.log("resultDeleteRentPeakSeason", resultDeleteRentPeakSeason);

        alert.setAlert(AlertType.SUCCESS, "성수기가 삭제 되었습니다.", "");

        requestGetRentPeakSeasons();
    }, [resultDeleteRentPeakSeason]);

    useEffect(() => {
        if (errorDeleteRentPeakSeason === null) return;
        console.log("errorDeleteRentPeakSeason", errorDeleteRentPeakSeason);
    }, [errorDeleteRentPeakSeason]);

    const onFinish = (values: any) => {
        if (!rentPeakSeasons || rentPeakSeasons.length === 0) {
            alert.setAlert(AlertType.INVALID_PARAM, "성수기를 먼저 등록하세요.", "");
            return;
        }
        requestUpdateRentPeakSeasonMessage(values.message);
    };

    const dayCellContent = useCallback(
        (arg: any) => {
            const isHoliday = holidays.find((holiday) => holiday.date === moment(arg.date).format(Common.FORMAT_DATE));
            const isSaturday = arg.date.getDay() === 6;
            const isSunday = arg.date.getDay() === 0;

            const dayNumberTextWithoutSuffix = arg.dayNumberText.replace("일", "");

            const peakSeasonClassName =
                rentPeakSeasons?.find((peakSeason) => moment(arg.date).startOf("day").isBetween(peakSeason.startDate, peakSeason.endDate, undefined, "[]")) !==
                undefined
                    ? styles.peakSeason
                    : "";

            if (isHoliday) {
                return (
                    <div className={peakSeasonClassName} style={{ textAlign: "center" }}>
                        <p style={{ color: "red", margin: "0" }}>{dayNumberTextWithoutSuffix}</p>
                        <p style={{ color: "red", margin: "0", marginTop: "1rem", fontSize: "10px" }}>{isHoliday.dateName}</p>
                    </div>
                );
            } else if (isSaturday) {
                return (
                    <span className={peakSeasonClassName} style={{ color: "blue" }}>
                        {dayNumberTextWithoutSuffix}
                    </span>
                );
            } else if (isSunday) {
                return (
                    <span className={peakSeasonClassName} style={{ color: "red" }}>
                        {dayNumberTextWithoutSuffix}
                    </span>
                );
            }

            return (
                <span className={peakSeasonClassName} style={{ color: "black" }}>
                    {dayNumberTextWithoutSuffix}
                </span>
            );
        },
        [holidays, rentPeakSeasons]
    );

    const handleDatesSet = (arg: any) => {
        const selectedYear = moment(arg.view.currentStart).year();

        if (currentYear !== selectedYear) {
            setCurrentYear(selectedYear);
            requestGetHolidays(selectedYear);
        }
    };

    const requestAxiosGetHolidays = async (year: string, month?: string) => {
        const response = await utilAxiosWithAuth().get(Request.RENT_HOLIDAYS_URL, { params: { year: year, month: month } });
        return response.data;
    };

    const {
        loading: loadingGetHolidays,
        error: errorGetHolidays,
        data: resultGetHolidays,
        execute: requestGetHolidays,
    } = useAsyncAxios(requestAxiosGetHolidays);

    useEffect(() => {
        if (resultGetHolidays === null) return;

        console.log("resultGetHolidays", resultGetHolidays);
        setHolidays(resultGetHolidays.holidays);
    }, [resultGetHolidays]);

    useEffect(() => {
        if (errorGetHolidays === null) return;

        console.log("errorGetHolidays", errorGetHolidays);
    }, [errorGetHolidays]);

    return (
        <div className="container">
            <Space size={30} direction="vertical" style={{ width: "100%" }}>
                <Row align={"middle"} justify={"space-between"}>
                    <h3 className="title">성수기 관리</h3>
                </Row>
                <Row gutter={20} className={styles.calendar}>
                    <Col span={24}>
                        <FullCalendar
                            locale="ko"
                            height="600px"
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            datesSet={handleDatesSet}
                            dayCellContent={dayCellContent}
                            dayHeaderContent={(arg) => <span style={{ color: "black" }}>{arg.text}</span>}
                        />
                    </Col>
                </Row>

                <Row justify={"space-between"} align={"middle"}>
                    <Col span={6}>
                        <Divider orientation="left">
                            <span>성수기 내역</span>
                        </Divider>
                    </Col>
                    <Col span={18} style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            onClick={() => {
                                setOpenModalRentPeakSeason(true);
                            }}
                        >
                            {String.userAdd}
                        </Button>
                    </Col>
                    <DataTable
                        hasScroll={false}
                        columns={columns}
                        rowKey="rentPeakSeasonId"
                        dataSource={rentPeakSeasons}
                        // onRow={(value) => setCurrentRentCar(value)}
                    />
                </Row>
            </Space>

            <div>
                <Form form={form} onFinish={onFinish}>
                    <Row justify={"space-between"} align={"middle"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>성수기 안내문구</span>
                            </Divider>
                        </Col>
                        <Col span={18} style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Form.Item style={{ margin: 0 }}>
                                <Button htmlType="submit">수정</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Space size={20} direction="vertical" style={{ width: "100%" }}>
                        <Row>
                            <Col span={24}>
                                <Form.Item className="searchForm" name="message" label="내용" rules={[{ required: true, message: "내용을 입력하세요." }]}>
                                    <TextArea
                                        rows={5}
                                        placeholder={rentPeakSeasons && rentPeakSeasons.length > 0 ? "달력에 표시되는 안내문구" : "성수기를 먼저 등록하세요."}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Space>
                </Form>
            </div>
            {openModalRentPeakSeason && (
                <ModalRentPeakSeason
                    open={openModalRentPeakSeason}
                    rentPeakSeason={currentRentPeakSeason}
                    onChangedOpen={(open: boolean) => {
                        setOpenModalRentPeakSeason(open);
                        setCurrentRentPeakSeason(undefined);
                    }}
                    onChangeRentPeakSeasonInfo={requestRegisterUpdateRentPeakSeason}
                />
            )}
        </div>
    );
}

export default RentPeakSeasonManagement;
